import { formatTime } from "@clipboard-health/date-time";
import { Illustration, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { CardContent, Skeleton, Stack } from "@mui/material";
import {
  CLIPBOARD_SCORE,
  DEFAULT_CLOCK_IN_BONUS_POINTS,
} from "@src/appV2/ClipboardScore/constants";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useArrivalTimeConfig } from "@src/appV2/redesign/ArrivalTime/useArrivalTimeConfig";
import { Button } from "@src/appV2/redesign/components/Button";
import { useOpenGeoLocationContext } from "@src/appV2/redesign/OpenGeoLocation/useOpenGeoLocation";
import { formatTravelTimeForDisplay } from "@src/appV2/redesign/utils/formatTravelTimeForDisplay";
import { differenceInHours, parseISO } from "date-fns";

import { ShiftDetailsInnerCard } from "../InnerCard";
import type { ArrivalContext, ClockInContext, ParkingContext } from "./types";

export interface ParkingCardContentProps {
  arrivalContext: ArrivalContext;
  clockInContext: ClockInContext;
  parkingContext: ParkingContext;
  estimatedTravelTime?: number;
  isEstimatedTravelTimeLoading: boolean;
  shiftStart: string;
  isNfcShift: boolean;
  isSecondShiftInDouble: boolean;
  workplaceTimezone: string;
  currentDate: Date;
  isClipboardScoreFrozen: boolean;
}

export function ParkingCardContent(props: ParkingCardContentProps) {
  const {
    arrivalContext,
    clockInContext,
    parkingContext,
    estimatedTravelTime,
    isEstimatedTravelTimeLoading,
    shiftStart,
    isNfcShift,
    isSecondShiftInDouble,
    workplaceTimezone,
    currentDate,
    isClipboardScoreFrozen,
  } = props;

  const { href, onClick: onGetDirections } = useOpenGeoLocationContext();

  const enforceNfcTimesheetBounds =
    useCbhFlag(CbhFeatureFlag.ENFORCE_NFC_TIMESHEET_BOUNDS, {
      defaultValue: false,
    }) && isNfcShift;

  const { useExpectedArrivalTime } = useArrivalTimeConfig();

  const getArrivedMessage = () => {
    const now = currentDate;
    const shiftStartTime = parseISO(shiftStart);
    const hoursUntilShift = differenceInHours(shiftStartTime, now);

    if (hoursUntilShift > 24) {
      return "You've arrived!";
    }

    if (isDefined(clockInContext.earliestClockInTime) && isDefined(workplaceTimezone)) {
      const formattedClockInTime = formatTime(clockInContext.earliestClockInTime, {
        timeZone: workplaceTimezone,
      });
      return `You've arrived! You'll be able to clock in at ${formattedClockInTime}`;
    }

    if (isSecondShiftInDouble || isClipboardScoreFrozen) {
      if (enforceNfcTimesheetBounds) {
        return "You've arrived! Clock in to your shift to begin earning";
      }

      return "You've arrived! Clock in to your shift";
    }

    const nfcSuffix = enforceNfcTimesheetBounds ? " and begin earning" : "";

    // Handle different timing windows with appropriate messaging
    if (clockInContext.isAlreadyLate) {
      return `You've arrived! Clock in now to minimize ${CLIPBOARD_SCORE} penalties${nfcSuffix}`;
    }

    if (clockInContext.isInGracePeriod) {
      return `You've arrived! Clock in now to avoid ${CLIPBOARD_SCORE} penalties${nfcSuffix}`;
    }

    if (clockInContext.isInBonusWindow) {
      return `You've arrived! Clock in now to earn ${DEFAULT_CLOCK_IN_BONUS_POINTS} ${CLIPBOARD_SCORE}${nfcSuffix}`;
    }

    if (useExpectedArrivalTime && clockInContext.isInPreArrivalWindow) {
      return `You've arrived! Clock in now to earn ${DEFAULT_CLOCK_IN_BONUS_POINTS} bonus ${CLIPBOARD_SCORE} at the end of the shift`;
    }

    if (clockInContext.isInPreArrivalWindow) {
      return `You've arrived! Clock in to your shift on time to earn ${DEFAULT_CLOCK_IN_BONUS_POINTS} ${CLIPBOARD_SCORE}${nfcSuffix}`;
    }

    // Default case: before shift start, not in pre-arrival window
    return `You've arrived! Clock in to your shift on time to earn ${DEFAULT_CLOCK_IN_BONUS_POINTS} ${CLIPBOARD_SCORE}${nfcSuffix}`;
  };

  const getDisplayMessage = () => {
    switch (arrivalContext.state) {
      case "arrived": {
        return getArrivedMessage();
      }

      case "traveling": {
        return isDefined(estimatedTravelTime)
          ? `You are a ${formatTravelTimeForDisplay(Math.max(estimatedTravelTime, 1))} drive away`
          : "";
      }

      case "ready-to-travel": {
        return isDefined(estimatedTravelTime)
          ? `${formatTravelTimeForDisplay(estimatedTravelTime)} drive from home`
          : "";
      }

      case "not-ready-to-travel": {
        return "Your shift is scheduled";
      }

      case "unknown": {
        return "Location unavailable";
      }

      default: {
        return "";
      }
    }
  };

  const shouldShowGetDirections = arrivalContext.state !== "arrived";

  return (
    <ShiftDetailsInnerCard
      footer={
        shouldShowGetDirections && (
          <Button
            fullWidth
            variant="outlined"
            size="medium"
            onClick={() => {
              logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_GET_DIRECTIONS_TAPPED, {
                arrivalState: arrivalContext.state,
              });
              if (onGetDirections) {
                onGetDirections();
              } else {
                window.open(href, "_blank");
              }
            }}
          >
            Get directions
          </Button>
        )
      }
    >
      <CardContent>
        <Stack spacing={4} alignItems="center" sx={{ paddingX: 4, paddingY: 4 }}>
          <Illustration type="driving" />

          {isEstimatedTravelTimeLoading ? (
            <Stack direction="column" justifyContent="center" alignItems="center">
              <Skeleton variant="text" width={150} height={25} data-testid="travel-time-loading" />
            </Stack>
          ) : (
            <Text semibold variant="h5" sx={{ textAlign: "center" }} data-testid="travel-time-text">
              {getDisplayMessage()}
            </Text>
          )}

          {parkingContext.isLoading && (
            <Skeleton
              variant="text"
              width={200}
              height={20}
              data-testid="parking-instructions-loading"
            />
          )}

          {isDefined(parkingContext.instructions) && (
            <Text
              variant="body2"
              sx={{ textAlign: "center" }}
              data-testid="parking-instructions-text"
            >
              Parking instructions: {parkingContext.instructions}
            </Text>
          )}
        </Stack>
      </CardContent>
    </ShiftDetailsInnerCard>
  );
}
